import { fromEvent, merge, Observable, of } from "rxjs";
import { concatMap, filter, switchMap, tap } from "rxjs/operators";
import { ExtendedMediaStream } from "../extended-media-stream";
import { mapVideoToCanvas } from "./mapVideoToCanvas";

export function mapStreamToCanvas(
  stream: ExtendedMediaStream,
  canvas: HTMLCanvasElement,
  videoFactory: () => HTMLVideoElement,
  options?: { toFlip: boolean }
) {
  return new Observable<void>((subscriber) => {
    // Safari's `muted` logic has weird behavior and video object can be unmuted in some cases.
    // In order to fix this issue, create temporary MediaStream object for the video tracks only.

    // Don't assign an empty stream to the srcObject
    // srcObject = new MediaStream()
    // it's laggy in Chrome

    const video = videoFactory();
    video.muted = true;
    video.srcObject = stream.videoTracks.length
      ? new MediaStream(stream.videoTracks)
      : null;

    // In order to handle
    // AbortError: The play() request was interrupted by a new load request.
    // for the stream without the video track
    const sub0 = fromEvent(video, "canplay")
      .pipe(
        concatMap(() => video.play()),
        switchMap(() =>
          mapVideoToCanvas(video, canvas, options).pipe(
            tap(() => subscriber.next())
          )
        )
      )
      .subscribe();

    const sub1 = merge(
      of(void 0),
      stream.addTrack$.pipe(
        filter((_) => _.kind === "video"),
        tap((_) => {
          video.srcObject = video.srcObject || new MediaStream();
          (video.srcObject as MediaStream).addTrack(_);
        })
      ),
      stream.removeTrack$.pipe(
        filter((_) => _.kind === "video"),
        tap((_) => {
          (video.srcObject as MediaStream)?.removeTrack?.(_);
        })
      )
    )
      .pipe(
        // 'load' call fires the 'canplay' event
        tap(() => video.load())
      )
      .subscribe();

    return () => {
      sub0.unsubscribe();
      sub1.unsubscribe();

      video.pause();
      video.srcObject = null;
      video.load();
    };
  });
}
